import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { films } from '@/mocks/films';
import { countByType } from '@/lib/film';
import { useSeo } from '@/hooks/useSeo';

const services = [
  {
    icon: 'ri-database-2-line',
    title: '结构化的影视资料',
    desc: '每部作品都整理导演、主演、类型、地区、年份与简介，详情页资料与列表卡片保持一致。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '多维筛选与搜索',
    desc: '类型、地区、年份自由组合，配合关键词搜索，几秒钟就能把想看的内容范围缩小。',
  },
  {
    icon: 'ri-bookmark-3-line',
    title: '本地收藏清单',
    desc: '喜欢的作品可以一键收藏，状态保存在你自己的浏览器里，刷新和下次访问都还在。',
  },
];

const faqs = [
  {
    q: '青青草能直接看电影吗？',
    a: '不能。本站是影视资料的整理与推荐平台，不提供播放、下载或会员服务，页面内所有作品均为演示数据。',
  },
  {
    q: '收藏会同步到其他设备吗？',
    a: '不会。收藏保存在当前浏览器的本地存储中，清除浏览器数据或更换设备后将不再保留。',
  },
  {
    q: '作品信息多久更新一次？',
    a: '演示数据为固定内容，不会自动更新。真实运营时需要接入内容管理系统或数据库。',
  },
];

export default function AboutPage() {
  const location = useLocation();

  useSeo({
    title: '关于我们 - 青青草影视资源平台',
    description:
      '了解青青草影视资源平台的定位与服务：整理电影与电视剧资料，提供分类筛选、搜索与资讯推荐，并说明使用方式、版权声明与联系方式。',
    keywords: '青青草,关于我们,影视资源平台,版权声明,联系方式',
  });

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.replace('#', ''));
    if (target) {
      window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    }
  }, [location.hash]);

  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20cinema%20hall%20with%20rows%20of%20seats%20and%20a%20glowing%20screen%2C%20deep%20navy%20tones%20with%20warm%20amber%20accent%20light%2C%20atmospheric%20film%20still&width=1920&height=720&seq=qqc-about-bg&orientation=landscape"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/40" />
        <div className="relative mx-auto w-full max-w-portal px-4 py-14 md:px-6 md:py-20">
          <nav className="mb-5 flex items-center gap-1.5 text-xs text-background-400" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-200">关于我们</span>
          </nav>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-300/40 bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-200">
            <i className="ri-leaf-line text-sm" />
            About Qingqingcao
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            关于青青草 · 认真整理每一部值得看的光影
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            我们相信，找片不该是一件费劲的事。青青草用清晰的分类、干净的排版和诚实的资料，
            把电影与电视剧的内容整理清楚，让选择变得从容一点。
          </p>
          <div className="mt-8 flex flex-wrap gap-6">
            {[
              { value: films.length, label: '演示作品' },
              { value: countByType('movie'), label: '电影' },
              { value: countByType('tv'), label: '电视剧' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-heading text-2xl font-bold text-accent-300">{stat.value}</p>
                <p className="mt-1 text-xs text-background-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-portal px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <span className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent-600">
              <span className="h-2 w-2 rounded-full bg-accent-500" />
              Our Positioning
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">平台定位</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              青青草是一个面向影视爱好者的内容型平台。我们把电影与电视剧的基础资料整理成统一的结构：
              类型、地区、年份、导演、主演、时长与剧情简介，让用户可以按自己的习惯去查找，而不是被动地刷信息流。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              在内容呈现上，我们尽量克制：不做夸张的推荐语，不堆砌无关信息，只用清楚的分类与完整的详情，
              帮助你在几分钟内判断“这部值不值得看”。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              需要说明的是，本站是产品演示站点。所有影视作品、评分、热度与资讯内容均为虚构示例，
              用于展示页面结构与交互方式，不构成任何真实的内容推荐。
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5 md:p-6">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-service-line text-base text-primary-600" />
                我们提供什么
              </h2>
              <ul className="space-y-4">
                {services.map((service) => (
                  <li key={service.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-base text-secondary-700">
                      <i className={service.icon} />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">{service.title}</h3>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-500">{service.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="usage" className="scroll-mt-24 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-portal px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">使用说明</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-500">
            下面是几个常见问题的说明，如果你第一次使用青青草，可以先从这里开始。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
            {faqs.map((faq) => (
              <div
                key={faq.q}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <h3 className="flex items-start gap-2 font-heading text-sm font-semibold text-foreground-900">
                  <i className="ri-question-line mt-0.5 text-base text-accent-600" />
                  <span>{faq.q}</span>
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500">{faq.a}</p>
              </div>
            ))}
          </div>

          <div id="copyright" className="mt-6 scroll-mt-24 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
              <i className="ri-copyright-line text-lg" />
              版权声明与内容来源
            </h3>
            <ul className="mt-3 space-y-2 text-xs leading-relaxed text-secondary-800 md:text-sm">
              <li>1. 站点内的片名、简介、评分、热度及资讯均为虚构演示内容，不对应任何真实影视作品。</li>
              <li>2. 页面配图由 AI 生成，仅用于版式演示，不代表真实剧照或海报。</li>
              <li>3. 本站不提供影片播放、下载、字幕或会员服务，也不存储任何影视文件。</li>
              <li>4. 如需将本页面用于正式业务，请自行准备已获授权的作品资料与图片素材。</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-portal px-4 py-12 md:px-6 md:py-16">
        <div id="contact" className="grid scroll-mt-24 grid-cols-1 gap-6 rounded-lg border border-background-200 bg-primary-50/60 p-6 md:grid-cols-12 md:p-10">
          <div className="md:col-span-7">
            <span className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent-600">
              <span className="h-2 w-2 rounded-full bg-accent-500" />
              Contact
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              如果你对内容整理方式有建议，或者发现了资料错误，欢迎写信告诉我们。
              我们会在工作日集中处理内容反馈。
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <a
                href="mailto:hello@qingqingcao.example"
                className="flex w-fit cursor-pointer items-center gap-2 text-primary-700 transition-colors hover:text-primary-800"
              >
                <i className="ri-mail-send-line text-base" />
                hello@qingqingcao.example
              </a>
              <p className="flex items-center gap-2 text-foreground-600">
                <i className="ri-map-pin-line text-base text-foreground-400" />
                内容合作与反馈：请通过上方邮箱提交，暂不设电话与在线客服。
              </p>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-900">反馈小贴士</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                写信时附上作品名称、页面链接与具体问题，可以让我们更快定位内容。涉及版权的反馈，
                请一并说明权利归属与希望的处理方式。
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to="/category?type=movie"
                  className="flex cursor-pointer items-center justify-between rounded-md border border-background-200 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  先去逛逛电影分类
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/search"
                  className="flex cursor-pointer items-center justify-between rounded-md border border-background-200 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  搜索想看的内容
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}